// Slow/stuck query reporting — the staging-diagnosable half of the DB logger.
//
// `create-performance-logger`'s existing logging is `isDevelopment()`-gated and
// fires only AFTER a query resolves. Neither property helps with the failure we
// are actually chasing: on staging, advancing to the next card sometimes parks
// forever in its loading state while the rest of the app stays usable. That is a
// promise that never settles, on a build where console logs go nowhere.
//
// So this module reports on two edges:
//
//   1. WATCHDOG (the important one) — a timer armed when the query STARTS. If the
//      query is still in flight at the threshold, we report immediately, while it
//      is stuck. A hung query never reaches its own completion handler, so
//      after-the-fact timing can never see the very case we need to see.
//   2. COMPLETION — a query that finished, but took longer than the threshold.
//      Reported as a span/breadcrumb rather than an issue: slow-but-finished is a
//      performance data point, not a fault.
//
// Everything here is failure-tolerant: instrumentation must never be the reason a
// query fails, so every report path is wrapped and swallowed.

import { captureSentryException } from "@-/dev.improve.haniverse.lib.sentry";
import { PERF_OP, recordPerfDuration } from "@-/dev.improve.haniverse.lib.sentry/perf";

/**
 * How long an individual query may run before it is considered anomalous.
 *
 * One second is far above any healthy local-SQLite read (the existing dev logger
 * colours anything past 20ms red and flags a 50ms scan as slow), so this fires
 * only on genuine pathology, not on ordinary variance.
 */
export const SLOW_QUERY_THRESHOLD_MS = 1000;

/**
 * A stuck query is reported once at the watchdog threshold and then again on a
 * geometric backoff, so a permanently-hung query leaves a trail showing it never
 * came back — without spamming Sentry once a minute forever. Escalation stops
 * after the last entry; by then "this never resolved" is established.
 */
const STUCK_REESCALATION_MS = [5000, 30_000] as const;

/** Sentry `fingerprint` root, so every stuck-query report groups by callsite. */
const STUCK_FINGERPRINT = "db-query-stuck";

export interface SlowQueryContext {
  /** Threaded `[file:line:col]` when available — the app frame that issued it. */
  callsite: string | null;
  /**
   * Which connection: `actorDb` / `contentDb` / `staticDb`. Null when the tag was
   * never stamped (a handle opened outside the usual factories), which still
   * groups usefully — the callsite and SQL identify it.
   */
  databaseName: string | null;
  /** `SELECT` / `INSERT` / …, cheap to derive and enough to classify the query. */
  operation: string;
  /** Parameterised SQL. Never the bind values (see `redactQueryText`). */
  queryText: string;
}

/**
 * Bind parameters can carry user content, so they are never sent. The SQL text
 * itself is already parameterised (`?` placeholders) and is truncated to keep
 * payloads small and grouping stable.
 */
function redactQueryText(queryText: string): string {
  const collapsed = queryText.replaceAll(/\s+/gu, " ").trim();
  return collapsed.length > 300 ? `${collapsed.slice(0, 300)}…` : collapsed;
}

function buildAttributes(context: SlowQueryContext, elapsedMs: number) {
  return {
    "han.db.callsite": context.callsite ?? "unknown",
    "han.db.elapsed_ms": elapsedMs,
    "han.db.name": context.databaseName ?? "unknown",
    "han.db.operation": context.operation,
    "han.db.query": redactQueryText(context.queryText),
  };
}

/** A short, stable label used for both the span name and issue grouping. */
function describeQuery(context: SlowQueryContext): string {
  return `${context.databaseName ?? "unknown"} ${context.operation} ${context.callsite ?? "unknown"}`;
}

/**
 * Report a query that is STILL RUNNING at `elapsedMs`. This is an error-level
 * capture (not a span) because an in-flight query past the threshold is a fault
 * signal, and errors carry the stack/breadcrumb context needed to see what the
 * app was doing when it wedged.
 */
function reportStuckQuery(context: SlowQueryContext, elapsedMs: number): void {
  const error = new Error(
    `Database query still running after ${elapsedMs}ms: ${describeQuery(context)}`,
  );
  error.name = "SlowQueryWatchdog";

  captureSentryException(error, {
    contexts: {
      "han.db": {
        callsite: context.callsite ?? "unknown",
        database: context.databaseName,
        elapsedMs,
        operation: context.operation,
        query: redactQueryText(context.queryText),
        state: "in-flight",
      },
    },
    // Group by callsite + operation rather than by message: the elapsed figure is
    // in the message and would otherwise split one recurring hang into many issues.
    fingerprint: [
      STUCK_FINGERPRINT,
      context.databaseName ?? "unknown",
      context.operation,
      context.callsite ?? "unknown",
    ],
    level: "error",
    tags: {
      "han.db.name": context.databaseName ?? "unknown",
      "han.db.operation": context.operation,
      "han.db.stuck": "true",
    },
  });
}

/**
 * Report a query that FINISHED but exceeded the threshold. Emitted as a perf span
 * so it lands on the same trace waterfall as the rest of the `han.db` timings and
 * is attributed to the screen the user was on.
 */
function reportSlowCompletion(
  context: SlowQueryContext,
  durationMs: number,
  outcome: "resolved" | "rejected",
): void {
  recordPerfDuration(
    `slow query ${describeQuery(context)}`,
    {
      attributes: { ...buildAttributes(context, durationMs), "han.db.outcome": outcome },
      op: PERF_OP.db,
    },
    durationMs,
  );
}

/**
 * The two sinks a tracked query reports to. Injectable so the timing/escalation
 * logic can be exercised against real observable calls in tests without mocking
 * the Sentry package; production always uses {@link defaultSlowQueryReporter}.
 */
export interface SlowQueryReporter {
  onSlowCompletion: (
    context: SlowQueryContext,
    durationMs: number,
    outcome: "resolved" | "rejected",
  ) => void;
  onStuck: (context: SlowQueryContext, elapsedMs: number) => void;
}

export const defaultSlowQueryReporter: SlowQueryReporter = {
  onSlowCompletion: reportSlowCompletion,
  onStuck: reportStuckQuery,
};

/**
 * Instrument one query round trip.
 *
 * Returns a `settle(outcome)` callback the caller MUST invoke in a `finally`, which
 * disarms the watchdog and reports a slow-but-finished query. Instrumentation
 * failures are swallowed: a broken report must never break a working query.
 */
export function trackQuery(
  getContext: () => SlowQueryContext,
  reporter: SlowQueryReporter = defaultSlowQueryReporter,
): (outcome: "resolved" | "rejected") => void {
  const startTime = Date.now();
  let settled = false;
  // Resolved lazily and memoised: building the context costs a regex over the SQL,
  // which is wasted work on the overwhelming majority of queries that finish fast.
  let context: SlowQueryContext | null = null;
  const resolveContext = (): SlowQueryContext => (context ??= getContext());

  const timers: ReturnType<typeof setTimeout>[] = [];
  const armEscalation = (delayMs: number): void => {
    timers.push(
      setTimeout(() => {
        if (settled) {
          return;
        }
        try {
          reporter.onStuck(resolveContext(), Date.now() - startTime);
        } catch {
          // Instrumentation must never surface as a query failure.
        }
      }, delayMs),
    );
  };

  armEscalation(SLOW_QUERY_THRESHOLD_MS);
  for (const extraDelayMs of STUCK_REESCALATION_MS) {
    armEscalation(SLOW_QUERY_THRESHOLD_MS + extraDelayMs);
  }

  return (outcome) => {
    if (settled) {
      return;
    }
    settled = true;
    for (const timer of timers) {
      clearTimeout(timer);
    }

    const durationMs = Date.now() - startTime;
    if (durationMs < SLOW_QUERY_THRESHOLD_MS) {
      return;
    }
    try {
      reporter.onSlowCompletion(resolveContext(), durationMs, outcome);
    } catch {
      // As above — never let reporting break the caller.
    }
  };
}
